import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertCircle,
  Archive,
  Calendar,
  Calendar as CalendarIcon,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  ExternalLink,
  FileText,
  Globe,
  Mail,
  MapPin,
  MessageSquare,
  Pause,
  Phone,
  Shield,
  Users,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsLeadgenFormList,
  type FacebookAdsLeadgenFormListResult,
} from 'sprites-types/workflow/tools/facebookAdsLeadgenFormList'
import { SearchableDropdown } from '../../ui/SearchableDropdown'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function FacebookAdsLeadgenFormListDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [selectedFormId, setSelectedFormId] = useState<string>('')
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [newlyLoaded, setNewlyLoaded] = useState(true)
  const [prevFormCount, setPrevFormCount] = useState(0)

  const schema = getToolResultSchema(facebookAdsLeadgenFormList, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  const result = parseResult.success ? (parseResult.data as FacebookAdsLeadgenFormListResult) : null
  const forms = result?.forms || []

  // Handle loading states and form updates - moved before early return
  useEffect(() => {
    if (forms.length > 0) {
      setIsLoading(false)

      // Check if we have new forms
      if (forms.length !== prevFormCount) {
        setNewlyLoaded(true)
        setPrevFormCount(forms.length)

        // Clear the "newly loaded" state after animation
        const timer = setTimeout(() => {
          setNewlyLoaded(false)
        }, 2000)

        return () => clearTimeout(timer)
      }
    }
  }, [forms.length, prevFormCount])

  // Show loading state initially - moved before early return
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 800)

    return () => clearTimeout(timer)
  }, [])

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  // Helper function to toggle section expansion
  const toggleSection = (formId: string, section: string) => {
    const key = `${formId}-${section}`
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  // Helper function to check if section is expanded
  const isSectionExpanded = (formId: string, section: string) => {
    const key = `${formId}-${section}`
    return expandedSections[key] || false
  }

  // Helper function to get status icon and color
  const getStatusDisplay = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50' }
      case 'PAUSED':
        return { icon: Pause, color: 'text-yellow-600', bgColor: 'bg-yellow-50' }
      case 'ARCHIVED':
        return { icon: Archive, color: 'text-gray-600', bgColor: 'bg-gray-50' }
      default:
        return { icon: AlertCircle, color: 'text-gray-500', bgColor: 'bg-gray-50' }
    }
  }

  // Helper function to get question type icon
  const getQuestionTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'EMAIL':
        return Mail
      case 'PHONE':
      case 'PHONE_NUMBER':
        return Phone
      case 'FULL_NAME':
      case 'FIRST_NAME':
      case 'LAST_NAME':
        return Users
      case 'DATE_OF_BIRTH':
      case 'DATE':
        return CalendarIcon
      case 'ZIP_CODE':
      case 'CITY':
      case 'STATE':
      case 'COUNTRY':
        return MapPin
      case 'WEBSITE':
        return Globe
      case 'CUSTOM':
        return MessageSquare
      default:
        return FileText
    }
  }

  // Helper function to format question type display name
  const formatQuestionType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  // Helper function to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  // Get selected form for detailed view
  const selectedForm = selectedFormId ? forms.find((f) => f.id === selectedFormId) : null

  if (isLoading) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            >
              <FaFacebook className="h-5 w-5 text-blue-500" />
            </motion.div>
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">Loading Leadgen Forms...</div>
            <div className="text-xs text-gray-500">Fetching forms from Facebook</div>
          </div>
        </div>
      </div>
    )
  }

  if (forms.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]"
      >
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <FaFacebook className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No leadgen forms found</div>
        </div>
      </motion.div>
    )
  }

  // Extract page ID from workflow state if available
  const inputs = nodeExecution.inputs.functionArgs as Record<string, unknown> | undefined
  const workflowState = inputs?.workflowState as Record<string, unknown> | undefined
  const credentials = workflowState?.accountCredentials as Record<string, any> | undefined
  const pageId = credentials?.facebook?.selectedPageId as string | undefined

  // Header context menu actions
  const headerActions: ContextAction[] = [
    ...(pageId
      ? [
          {
            id: 'open-page-publishing',
            label: 'Open Page Publishing Tools',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () =>
              window.open(
                `https://business.facebook.com/page/${pageId}/publishing_tools/`,
                '_blank',
                'noopener,noreferrer'
              ),
          },
        ]
      : []),
    {
      id: 'copy-all-ids',
      label: 'Copy All Form IDs',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(forms.map((f) => f.id).join('\n')),
    },
  ]

  return (
    <motion.div
      className="space-y-3"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {/* New forms loaded indicator */}
      <AnimatePresence>
        {newlyLoaded && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="rounded-xl border border-blue-200/60 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 px-4 py-2.5"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="h-4 w-4 text-blue-600/80" />
              <div>
                <div className="text-sm font-medium text-blue-900/90">Forms Updated</div>
                <div className="text-xs text-blue-700/80">
                  Loaded {forms.length} form{forms.length !== 1 ? 's' : ''} from Facebook
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form selector dropdown */}
      {forms.length > 1 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm backdrop-blur-[8px]"
        >
          <div className="px-4 py-3">
            <label className="mb-2 block text-xs font-medium text-gray-500">
              SELECT FORM FOR DETAILS
            </label>
            <SearchableDropdown
              items={[
                { id: '', name: 'View all forms...', formName: 'View all forms...', status: '' },
                ...forms.map((form) => ({
                  id: form.id,
                  name: form.name,
                  formName: form.name,
                  status: form.status || 'Unknown Status',
                })),
              ]}
              selectedId={selectedFormId}
              onSelect={setSelectedFormId}
              placeholder="View all forms..."
              searchPlaceholder="Search forms by name..."
              icon={<FaFacebook className="h-4 w-4" />}
              selectedColor="blue"
              searchKey="name"
              renderItem={(item, isSelected) => (
                <div className="flex w-full min-w-0 items-center gap-3">
                  <div className="flex-shrink-0">
                    <FaFacebook
                      className={`h-4 w-4 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium">
                        {item.formName || item.name}
                      </span>
                      {isSelected && (
                        <div className="h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                      )}
                    </div>
                    {item.status && item.id && (
                      <div className="mt-0.5 text-xs text-gray-500">{item.status}</div>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        </motion.div>
      )}

      {/* Collapsible header */}
      <ContextMenuWrapper actions={headerActions}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <FaFacebook className="h-5 w-5 text-[#0064e0]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                Facebook Leadgen Forms
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {forms.length} form{forms.length !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {/* Detailed view for selected form or all forms */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {selectedForm ? (
              // Show selected form with full details
              <motion.div
                key={selectedForm.id}
                className="mx-8"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                {renderFormCard(selectedForm, true)}
              </motion.div>
            ) : (
              // Show all forms with enhanced details and staggered animations
              forms.map((form, idx) => (
                <motion.div
                  key={form.id}
                  className="mx-8"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: idx * 0.05,
                    duration: 0.3,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  {renderFormCard(form, false, idx)}
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )

  // Helper function to render individual form cards with enhanced details
  function renderFormCard(form: any, _isSelectedView: boolean = false, index: number = 0) {
    const statusDisplay = getStatusDisplay(form.status)
    const StatusIcon = statusDisplay.icon

    // Check if this specific form is currently selected
    const isCurrentlySelected = selectedFormId === form.id && selectedFormId !== ''

    // Add visual differentiation
    const borderColors = [
      'border-blue-200/60 hover:border-blue-300/80',
      'border-green-200/60 hover:border-green-300/80',
      'border-purple-200/60 hover:border-purple-300/80',
      'border-orange-200/60 hover:border-orange-300/80',
      'border-pink-200/60 hover:border-pink-300/80',
    ]
    const accentColors = [
      'from-blue-50/80 to-blue-100/60',
      'from-green-50/80 to-green-100/60',
      'from-purple-50/80 to-purple-100/60',
      'from-orange-50/80 to-orange-100/60',
      'from-pink-50/80 to-pink-100/60',
    ]

    const borderColor = isCurrentlySelected
      ? 'border-blue-400/80 ring-1 ring-blue-300/50'
      : borderColors[index % borderColors.length]
    const accentColor = accentColors[index % accentColors.length]

    const contextActions: ContextAction[] = [
      {
        id: 'copy-id',
        label: 'Copy Form ID',
        icon: <Copy className="h-4 w-4" />,
        onClick: () => navigator.clipboard.writeText(form.id),
      },
      ...(pageId
        ? [
            {
              id: 'view-in-business-manager',
              label: 'View in Business Manager',
              icon: <ExternalLink className="h-4 w-4" />,
              onClick: () =>
                window.open(
                  `https://business.facebook.com/latest/instant_forms/forms`,
                  '_blank',
                  'noopener,noreferrer'
                ),
            },
          ]
        : []),
    ]

    return (
      <ContextMenuWrapper actions={contextActions}>
        <div
          className={`rounded-3xl border ${borderColor} bg-white/85 text-black shadow-md backdrop-blur-[8px] transition-all duration-300 hover:bg-white/95 hover:shadow-lg`}
        >
          {/* Colored accent bar */}
          <div className={`h-0.5 w-full rounded-t-3xl bg-gradient-to-r ${accentColor}`}></div>

          <div className="flex items-start gap-4 px-4 py-3">
            <div className="mt-1 flex-shrink-0">
              <div className="rounded-full bg-blue-100 p-2 transition-colors duration-200 hover:bg-blue-200">
                <FaFacebook className="h-5 w-5 text-[#0064e0]" />
              </div>
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              {/* Form Header */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="mb-1 text-sm font-semibold text-gray-900">{form.name}</div>
                  <div className="flex items-center gap-2">
                    {form.status && (
                      <div
                        className={`flex items-center gap-1.5 rounded-full px-2 py-1 ${statusDisplay.bgColor}`}
                      >
                        <StatusIcon className={`h-3.5 w-3.5 ${statusDisplay.color}`} />
                        <span className={`text-xs font-medium ${statusDisplay.color}`}>
                          {form.status}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Details */}
              <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
                {/* Form ID */}
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                    Form ID
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(form.id)}
                    className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 transition-colors hover:border-blue-300 hover:bg-blue-50"
                    title="Click to copy Form ID"
                  >
                    <span className="font-mono text-xs font-medium text-gray-700 group-hover:text-blue-700">
                      {form.id}
                    </span>
                    <Copy className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-600" />
                  </button>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Lead Count */}
                  {typeof form.leadsCount === 'number' && (
                    <div className="flex items-center gap-2">
                      <div className="rounded-lg bg-blue-50 p-2">
                        <Users className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Total Leads</div>
                        <div className="font-semibold text-gray-900">
                          {form.leadsCount.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Created Date */}
                  {form.createdTime && (
                    <div className="flex items-center gap-2">
                      <div className="rounded-lg bg-green-50 p-2">
                        <Calendar className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Created</div>
                        <div className="font-semibold text-gray-900">
                          {formatDate(form.createdTime)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Info */}
                {form.locale && (
                  <div className="border-t border-gray-100 pt-2">
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Locale:</span> {form.locale}
                    </div>
                  </div>
                )}
              </div>

              {/* Questions Section */}
              {form.questions && form.questions.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white">
                  <button
                    onClick={() => toggleSection(form.id, 'questions')}
                    className="flex w-full items-center justify-between p-3 text-left hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-900">
                        Questions ({form.questions.length})
                      </span>
                    </div>
                    {isSectionExpanded(form.id, 'questions') ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </button>

                  {isSectionExpanded(form.id, 'questions') && (
                    <div className="space-y-2 border-t border-gray-100 p-3">
                      {form.questions.map((question: any, idx: number) => {
                        const QuestionIcon = getQuestionTypeIcon(question.type)
                        return (
                          <div
                            key={idx}
                            className="flex items-start gap-3 rounded-lg bg-gray-50 p-2.5"
                          >
                            <div className="mt-0.5 flex-shrink-0">
                              <QuestionIcon className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-900">
                                  {formatQuestionType(question.type)}
                                </span>
                                {question.key && (
                                  <span className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-xs text-blue-700">
                                    {question.key}
                                  </span>
                                )}
                              </div>
                              {question.label && (
                                <div className="mb-1 text-xs text-gray-600">{question.label}</div>
                              )}
                              {question.options && question.options.length > 0 && (
                                <div className="space-y-1">
                                  <div className="text-xs font-medium text-gray-500">Options:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {question.options.map((option: any, optIdx: number) => (
                                      <span
                                        key={optIdx}
                                        className="inline-flex items-center rounded-full border bg-white px-2 py-0.5 text-xs text-gray-700"
                                      >
                                        {option.value}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Privacy Policy Section */}
              {form.privacyPolicyUrl && (
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-900">Privacy Policy</span>
                  </div>
                  <a
                    href={form.privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>View Privacy Policy</span>
                  </a>
                </div>
              )}

              {/* Context Card Section */}
              {form.contextCard && (
                <div className="rounded-xl border border-gray-200 bg-white">
                  <button
                    onClick={() => toggleSection(form.id, 'contextCard')}
                    className="flex w-full items-center justify-between p-3 text-left hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium text-gray-900">Context Card</span>
                    </div>
                    {isSectionExpanded(form.id, 'contextCard') ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </button>

                  {isSectionExpanded(form.id, 'contextCard') && (
                    <div className="space-y-2 border-t border-gray-100 p-3">
                      {form.contextCard.title && (
                        <div>
                          <div className="mb-1 text-xs font-medium text-gray-500">Title:</div>
                          <div className="text-sm text-gray-900">{form.contextCard.title}</div>
                        </div>
                      )}
                      {form.contextCard.content && form.contextCard.content.length > 0 && (
                        <div>
                          <div className="mb-1 text-xs font-medium text-gray-500">Content:</div>
                          <ul className="space-y-1">
                            {form.contextCard.content.map((item: string, idx: number) => (
                              <li
                                key={idx}
                                className="flex items-start gap-1 text-sm text-gray-700"
                              >
                                <span className="text-gray-400">•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {form.contextCard.style && (
                        <div>
                          <div className="mb-1 text-xs font-medium text-gray-500">Style:</div>
                          <span className="inline-block rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                            {form.contextCard.style}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Thank You Page Section */}
              {form.thankYouPage && (
                <div className="rounded-xl border border-gray-200 bg-white">
                  <button
                    onClick={() => toggleSection(form.id, 'thankYou')}
                    className="flex w-full items-center justify-between p-3 text-left hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-900">Thank You Page</span>
                    </div>
                    {isSectionExpanded(form.id, 'thankYou') ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </button>

                  {isSectionExpanded(form.id, 'thankYou') && (
                    <div className="space-y-3 border-t border-gray-100 p-3">
                      {form.thankYouPage.title && (
                        <div>
                          <div className="mb-1 text-xs font-medium text-gray-500">Title:</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {form.thankYouPage.title}
                          </div>
                        </div>
                      )}
                      {form.thankYouPage.body && (
                        <div>
                          <div className="mb-1 text-xs font-medium text-gray-500">Message:</div>
                          <div className="text-sm text-gray-700">{form.thankYouPage.body}</div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        {form.thankYouPage.button_type && (
                          <div>
                            <div className="mb-1 text-xs font-medium text-gray-500">
                              Button Type:
                            </div>
                            <span className="inline-block rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                              {form.thankYouPage.button_type.replace(/_/g, ' ')}
                            </span>
                          </div>
                        )}
                        {form.thankYouPage.button_text && (
                          <div>
                            <div className="mb-1 text-xs font-medium text-gray-500">
                              Button Text:
                            </div>
                            <div className="text-sm text-gray-900">
                              {form.thankYouPage.button_text}
                            </div>
                          </div>
                        )}
                      </div>
                      {form.thankYouPage.website_url && (
                        <div>
                          <div className="mb-1 text-xs font-medium text-gray-500">Website URL:</div>
                          <a
                            href={form.thankYouPage.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span>{form.thankYouPage.website_url}</span>
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Follow-up Actions Section */}
              {(form.followUpActionUrl || form.followUpActionText) && (
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-gray-900">Follow-up Actions</span>
                  </div>
                  <div className="space-y-2">
                    {form.followUpActionText && (
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-500">Action Text:</div>
                        <div className="text-sm text-gray-700">{form.followUpActionText}</div>
                      </div>
                    )}
                    {form.followUpActionUrl && (
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-500">Action URL:</div>
                        <a
                          href={form.followUpActionUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          <span>{form.followUpActionUrl}</span>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Custom Headline Section */}
              {form.questionPageCustomHeadline && (
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-900">Custom Headline</span>
                  </div>
                  <div className="text-sm text-gray-700">{form.questionPageCustomHeadline}</div>
                </div>
              )}

              {/* Legal Content Section */}
              {form.legalContent && (
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-gray-900">Legal Content</span>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    {typeof form.legalContent === 'string' ? (
                      <div>{form.legalContent}</div>
                    ) : (
                      <>
                        {form.legalContent.text && <div>{form.legalContent.text}</div>}
                        {form.legalContent.checkbox_text && (
                          <div className="flex items-start gap-2">
                            <input type="checkbox" disabled className="mt-0.5" />
                            <span>{form.legalContent.checkbox_text}</span>
                          </div>
                        )}
                        {form.legalContent.link_text && form.legalContent.link_url && (
                          <div>
                            <a
                              href={form.legalContent.link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline hover:text-blue-800"
                            >
                              {form.legalContent.link_text}
                            </a>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              {pageId && (
                <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
                  <a
                    href={`https://business.facebook.com/latest/instant_forms/forms`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-medium text-blue-700 hover:text-blue-900"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span>View in Business Manager</span>
                  </a>
                  <div className="mt-1.5 text-[10px] text-blue-600">
                    Search for form ID: {form.id}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ContextMenuWrapper>
    )
  }
}

function downloadFacebookAdsLeadgenFormList(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsLeadgenFormListToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsLeadgenFormList, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as FacebookAdsLeadgenFormListResult
  const forms = result.forms || []

  if (forms.length === 0) {
    return '## Facebook Leadgen Forms\n\nNo leadgen forms found.\n\n---\n'
  }

  let markdown = `## Facebook Leadgen Forms\n\n`
  markdown += `Found ${forms.length} form${forms.length !== 1 ? 's' : ''}:\n\n`

  forms.forEach((form) => {
    markdown += `### ${form.name}\n`
    markdown += `- **Form ID:** ${form.id}\n`
    if (form.status) markdown += `- **Status:** ${form.status}\n`
    if (typeof form.leadsCount === 'number')
      markdown += `- **Total Leads:** ${form.leadsCount.toLocaleString()}\n`
    if (form.createdTime) {
      const formattedDate = (() => {
        try {
          return new Date(form.createdTime).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        } catch {
          return form.createdTime
        }
      })()
      markdown += `- **Created:** ${formattedDate}\n`
    }
    if (form.locale) markdown += `- **Locale:** ${form.locale}\n`

    // Questions section
    if (form.questions && form.questions.length > 0) {
      markdown += `\n#### Questions (${form.questions.length})\n`
      form.questions.forEach((question, idx) => {
        markdown += `${idx + 1}. **${question.type
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')}**`
        if (question.key) markdown += ` (${question.key})`
        if (question.label) markdown += `: ${question.label}`
        markdown += '\n'
        if (question.options && question.options.length > 0) {
          markdown += '   - Options: ' + question.options.map((opt) => opt.value).join(', ') + '\n'
        }
      })
    }

    // Privacy Policy section
    if (form.privacyPolicyUrl) {
      markdown += `\n**Privacy Policy:** [View Policy](${form.privacyPolicyUrl})\n`
    }

    // Context Card section
    if (form.contextCard) {
      markdown += `\n#### Context Card\n`
      if (form.contextCard.title) markdown += `- **Title:** ${form.contextCard.title}\n`
      if (form.contextCard.style) markdown += `- **Style:** ${form.contextCard.style}\n`
      if (form.contextCard.content && form.contextCard.content.length > 0) {
        markdown += `- **Content:**\n`
        form.contextCard.content.forEach((item) => (markdown += `  - ${item}\n`))
      }
    }

    // Thank You Page section
    if (form.thankYouPage) {
      markdown += `\n#### Thank You Page\n`
      if (form.thankYouPage.title) markdown += `- **Title:** ${form.thankYouPage.title}\n`
      if (form.thankYouPage.body) markdown += `- **Message:** ${form.thankYouPage.body}\n`
      if (form.thankYouPage.button_type)
        markdown += `- **Button Type:** ${form.thankYouPage.button_type.replace(/_/g, ' ')}\n`
      if (form.thankYouPage.button_text)
        markdown += `- **Button Text:** ${form.thankYouPage.button_text}\n`
      if (form.thankYouPage.website_url)
        markdown += `- **Website:** [${form.thankYouPage.website_url}](${form.thankYouPage.website_url})\n`
    }

    // Follow-up Actions section
    if (form.followUpActionText || form.followUpActionUrl) {
      markdown += `\n#### Follow-up Actions\n`
      if (form.followUpActionText) markdown += `- **Action Text:** ${form.followUpActionText}\n`
      if (form.followUpActionUrl)
        markdown += `- **Action URL:** [${form.followUpActionUrl}](${form.followUpActionUrl})\n`
    }

    // Additional sections
    if (form.questionPageCustomHeadline)
      markdown += `\n**Custom Headline:** ${form.questionPageCustomHeadline}\n`
    if (form.legalContent) {
      if (typeof form.legalContent === 'string') {
        markdown += `\n**Legal Content:** ${form.legalContent}\n`
      } else {
        markdown += `\n**Legal Content:**\n`
        if (form.legalContent.text) markdown += `- Text: ${form.legalContent.text}\n`
        if (form.legalContent.checkbox_text)
          markdown += `- Checkbox: ${form.legalContent.checkbox_text}\n`
        if (form.legalContent.link_text && form.legalContent.link_url) {
          markdown += `- Link: [${form.legalContent.link_text}](${form.legalContent.link_url})\n`
        }
      }
    }

    markdown += '\n---\n\n'
  })

  markdown += '---\n'

  return markdown
}

export const FacebookAdsLeadgenFormListDisplay = createWorkflowToolComponent(
  FacebookAdsLeadgenFormListDisplayComponent,
  downloadFacebookAdsLeadgenFormList,
  facebookAdsLeadgenFormListToMarkdown
)
